import React, { useState, useMemo, useCallback } from 'react';
import { 
  Tooltip, 
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { TrendingUp, Award, GitCompare, Brain, Lightbulb, Target, Zap, Search, Plus, Lock, ListTodo, Loader2 } from 'lucide-react';
import { Card, Input, Drawer } from '../ui';
import useSocialMediaStore from '../../stores/socialMediaStore';
import { formatEMV } from '../../utils/emvCalculator';
import { useToast } from '../../contexts/ToastContext';
import Downloads from './Downloads';

interface CompetitorAnalysisInsight {
  summary: string;
  strengthsWeaknesses: {
    profile1Strengths: string[];
    profile1Weaknesses: string[];
    profile2Strengths: string[];
    profile2Weaknesses: string[];
  };
  recommendations: string[];
  keyFindings: string[];
}
import { scrapingService, ScrapeRequest } from '../../services/scrapingService'
import useScrapeOperationStore from '../../stores/scrapeOperationStore';
import { socialMediaService, CustomerAccount } from '../../services/socialMediaService';
import usePermissionsStore from '../../stores/permissionsStore';

const CompetitiveAnalysis: React.FC = () => {
  const { hasFeature, permissions, loading: permissionsLoading } = usePermissionsStore();
  const { showError, showSuccess } = useToast();
  const [activeTab, setActiveTab] = useState<'analysis' | 'downloads'>('analysis');
  
  const [selectedProfile1, setSelectedProfile1] = useState<string>('');
  const [selectedProfile2, setSelectedProfile2] = useState<string>('');
  const [aiInsights, setAiInsights] = useState<CompetitorAnalysisInsight | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);


  // Customer accounts state
  const [customerAccounts, setCustomerAccounts] = useState<CustomerAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  // Competitor limit enforcement
  const maxCompetitors = permissions?.effectiveLimits?.max_competitors ?? 0;
  const competitorCount = customerAccounts.filter((a: any) => a.categoryId === 3).length;
  const atCompetitorLimit = competitorCount >= maxCompetitors;

  // Add Account drawer state
  const [isAddProfileDrawerOpen, setIsAddProfileDrawerOpen] = useState(false);
  const [newProfileUsername, setNewProfileUsername] = useState('');
  const [newProfilePlatform, setNewProfilePlatform] = useState<'instagram' | 'tiktok'>('instagram');
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  
  // Profile data loading state
  const [loadingProfileData, setLoadingProfileData] = useState(false);
  
  // Individual profile analytics data
  const [profile1Analytics, setProfile1Analytics] = useState<any>(null);
  const [profile2Analytics, setProfile2Analytics] = useState<any>(null);
  // Profile posts removed - no longer needed

  const { 
    profiles, 
    posts, 
    currentCustomerId,
    loadDashboardAnalytics,
    loadTopPosts
  } = useSocialMediaStore();

  // Helper functions - getAccountsByPlatform removed as unused

  const getSelectedAccount = (accountId: string) => {
    return customerAccounts.find(account => account.id === accountId);
  };


  // Get selected accounts
  const selectedAccount1 = getSelectedAccount(selectedProfile1);
  const selectedAccount2 = getSelectedAccount(selectedProfile2);

  // Helper function to parse EMV values that come as strings like "$0.00"
  const parseEMV = (emvString: string): number => {
    if (!emvString) return 0;
    return parseFloat(emvString.replace(/[$,]/g, '')) || 0;
  };

  // Helper function to calculate engagement rate
  const calculateEngagementRate = (likes: number, comments: number, shares: number, views: number): number => {
    if (views === 0) return 0;
    return ((likes + comments + shares) / views) * 100;
  };

  // Get profile data - use loaded analytics data if available, otherwise fall back to profiles array
  const profile1Data = profile1Analytics?.metrics && selectedAccount1 
    ? {
        username: selectedAccount1.username,
        fullName: selectedAccount1.displayName,
        platform: selectedAccount1.platformName,
        totalEMV: parseEMV(profile1Analytics.metrics.totalEmv?.current),
        averageEMV: parseEMV(profile1Analytics.metrics.averageEmv?.current),
        totalPosts: profile1Analytics.metrics.totalPosts?.current || 0,
        totalViews: profile1Analytics.metrics.totalViews?.current || 0,
        totalLikes: profile1Analytics.metrics.totalLikes?.current || 0,
        totalComments: profile1Analytics.metrics.totalComments?.current || 0,
        totalShares: profile1Analytics.metrics.totalShares?.current || 0,
        collaboratorPosts: profile1Analytics.metrics.collaboratorPosts?.current || 0,
        collaboratorEMV: parseEMV(profile1Analytics.metrics.collaboratorEmv?.current),
        engagementRate: calculateEngagementRate(
          profile1Analytics.metrics.totalLikes?.current || 0,
          profile1Analytics.metrics.totalComments?.current || 0,
          profile1Analytics.metrics.totalShares?.current || 0,
          profile1Analytics.metrics.totalViews?.current || 0
        ),
        topPost: null, // Will be populated from posts data
        firstPost: '',
        lastPost: ''
      }
    : profiles.find(p => p.username === selectedAccount1?.username) || null;
    
  const profile2Data = profile2Analytics?.metrics && selectedAccount2 
    ? {
        username: selectedAccount2.username,
        fullName: selectedAccount2.displayName,
        platform: selectedAccount2.platformName,
        totalEMV: parseEMV(profile2Analytics.metrics.totalEmv?.current),
        averageEMV: parseEMV(profile2Analytics.metrics.averageEmv?.current),
        totalPosts: profile2Analytics.metrics.totalPosts?.current || 0,
        totalViews: profile2Analytics.metrics.totalViews?.current || 0,
        totalLikes: profile2Analytics.metrics.totalLikes?.current || 0,
        totalComments: profile2Analytics.metrics.totalComments?.current || 0,
        totalShares: profile2Analytics.metrics.totalShares?.current || 0,
        collaboratorPosts: profile2Analytics.metrics.collaboratorPosts?.current || 0,
        collaboratorEMV: parseEMV(profile2Analytics.metrics.collaboratorEmv?.current),
        engagementRate: calculateEngagementRate(
          profile2Analytics.metrics.totalLikes?.current || 0,
          profile2Analytics.metrics.totalComments?.current || 0,
          profile2Analytics.metrics.totalShares?.current || 0,
          profile2Analytics.metrics.totalViews?.current || 0
        ),
        topPost: null, // Will be populated from posts data
        firstPost: '',
        lastPost: ''
      }
    : profiles.find(p => p.username === selectedAccount2?.username) || null;

  // Check if only one account exists

  // Prepare EMV comparison data (large scale values)
  const emvComparisonData = useMemo(() => {
    if (!profile1Data || !profile2Data) return [];

    return [
      {
        metric: 'Total EMV',
        profile1: profile1Data.totalEMV,
        profile2: profile2Data.totalEMV
      },
      {
        metric: 'Average EMV',
        profile1: profile1Data.averageEMV,
        profile2: profile2Data.averageEMV
      },
      {
        metric: 'Collaborator EMV',
        profile1: profile1Data.collaboratorEMV,
        profile2: profile2Data.collaboratorEMV
      }
    ];
  }, [profile1Data, profile2Data]);

  // Prepare engagement comparison data (smaller scale values)
  const engagementComparisonData = useMemo(() => {
    if (!profile1Data || !profile2Data) return [];

    return [
      {
        metric: 'Total Posts',
        profile1: profile1Data.totalPosts,
        profile2: profile2Data.totalPosts
      },
      {
        metric: 'Total Views',
        profile1: profile1Data.totalViews,
        profile2: profile2Data.totalViews
      },
      {
        metric: 'Total Likes',
        profile1: profile1Data.totalLikes,
        profile2: profile2Data.totalLikes
      },
      {
        metric: 'Total Comments',
        profile1: profile1Data.totalComments,
        profile2: profile2Data.totalComments
      },
      {
        metric: 'Engagement Rate',
        profile1: profile1Data.engagementRate,
        profile2: profile2Data.engagementRate
      }
    ];
  }, [profile1Data, profile2Data]);

  // Combined data for the summary section
  const comparisonData = useMemo(() => {
    if (!profile1Data || !profile2Data) return [];

    return [
      ...emvComparisonData,
      {
        metric: 'Total Posts',
        profile1: profile1Data.totalPosts,
        profile2: profile2Data.totalPosts
      },
      {
        metric: 'Collaborator Posts',
        profile1: profile1Data.collaboratorPosts,
        profile2: profile2Data.collaboratorPosts
      },
      {
        metric: 'Total Views',
        profile1: profile1Data.totalViews,
        profile2: profile2Data.totalViews
      },
      ...engagementComparisonData.filter(d =>
        d.metric !== 'Total Posts' && d.metric !== 'Total Views'
      )
    ];
  }, [emvComparisonData, engagementComparisonData, profile1Data, profile2Data]);

  // Prepare radar chart data - Performance Overview
  const performanceRadarData = useMemo(() => {
    if (!profile1Data || !profile2Data) return [];

    // Normalize metrics to 0-100 scale for better visualization
    const maxEMV = Math.max(profile1Data.totalEMV, profile2Data.totalEMV);
    const maxPosts = Math.max(profile1Data.totalPosts, profile2Data.totalPosts);
    const maxEngagement = Math.max(profile1Data.engagementRate, profile2Data.engagementRate);
    const maxViews = Math.max(profile1Data.totalViews, profile2Data.totalViews);
    const maxLikes = Math.max(profile1Data.totalLikes, profile2Data.totalLikes);

    return [
      {
        metric: 'EMV',
        profile1: maxEMV > 0 ? (profile1Data.totalEMV / maxEMV) * 100 : 0,
        profile2: maxEMV > 0 ? (profile2Data.totalEMV / maxEMV) * 100 : 0
      },
      {
        metric: 'Posts',
        profile1: maxPosts > 0 ? (profile1Data.totalPosts / maxPosts) * 100 : 0,
        profile2: maxPosts > 0 ? (profile2Data.totalPosts / maxPosts) * 100 : 0
      },
      {
        metric: 'Engagement',
        profile1: maxEngagement > 0 ? (profile1Data.engagementRate / maxEngagement) * 100 : 0,
        profile2: maxEngagement > 0 ? (profile2Data.engagementRate / maxEngagement) * 100 : 0
      },
      {
        metric: 'Views',
        profile1: maxViews > 0 ? (profile1Data.totalViews / maxViews) * 100 : 0,
        profile2: maxViews > 0 ? (profile2Data.totalViews / maxViews) * 100 : 0
      },
      {
        metric: 'Likes',
        profile1: maxLikes > 0 ? (profile1Data.totalLikes / maxLikes) * 100 : 0,
        profile2: maxLikes > 0 ? (profile2Data.totalLikes / maxLikes) * 100 : 0
      }
    ];
  }, [profile1Data, profile2Data]);

  // Content Strategy Radar Data
  const contentStrategyRadarData = useMemo(() => {
    if (!profile1Data || !profile2Data) return [];

    const maxAvgEMV = Math.max(profile1Data.averageEMV, profile2Data.averageEMV);
    // maxSponsoredEMV removed as unused
    const maxEngagement = Math.max(profile1Data.engagementRate, profile2Data.engagementRate);
    const maxPosts = Math.max(profile1Data.totalPosts, profile2Data.totalPosts);
    
    // Calculate collaborator content percentage (using collaborator EMV as proxy)
    const profile1CollabPercent = profile1Data.totalEMV > 0 ? (profile1Data.collaboratorEMV / profile1Data.totalEMV) * 100 : 0;
    const profile2CollabPercent = profile2Data.totalEMV > 0 ? (profile2Data.collaboratorEMV / profile2Data.totalEMV) * 100 : 0;
    const maxCollabPercent = Math.max(profile1CollabPercent, profile2CollabPercent);

    return [
      {
        metric: 'Avg EMV',
        profile1: maxAvgEMV > 0 ? (profile1Data.averageEMV / maxAvgEMV) * 100 : 0,
        profile2: maxAvgEMV > 0 ? (profile2Data.averageEMV / maxAvgEMV) * 100 : 0
      },
      {
        metric: 'Collab %',
        profile1: maxCollabPercent > 0 ? (profile1CollabPercent / maxCollabPercent) * 100 : 0,
        profile2: maxCollabPercent > 0 ? (profile2CollabPercent / maxCollabPercent) * 100 : 0
      },
      {
        metric: 'Engagement',
        profile1: maxEngagement > 0 ? (profile1Data.engagementRate / maxEngagement) * 100 : 0,
        profile2: maxEngagement > 0 ? (profile2Data.engagementRate / maxEngagement) * 100 : 0
      },
      {
        metric: 'Volume',
        profile1: maxPosts > 0 ? (profile1Data.totalPosts / maxPosts) * 100 : 0,
        profile2: maxPosts > 0 ? (profile2Data.totalPosts / maxPosts) * 100 : 0
      },
      {
        metric: 'Consistency',
        // Using post frequency as a proxy for consistency (normalized)
        profile1: maxPosts > 0 ? Math.min((profile1Data.totalPosts / maxPosts) * 100, 100) : 0,
        profile2: maxPosts > 0 ? Math.min((profile2Data.totalPosts / maxPosts) * 100, 100) : 0
      }
    ];
  }, [profile1Data, profile2Data]);

  // Audience Engagement Radar Data
  const audienceEngagementRadarData = useMemo(() => {
    if (!profile1Data || !profile2Data) return [];

    // Calculate engagement rates
    const profile1CommentRate = profile1Data.totalViews > 0 ? (profile1Data.totalComments / profile1Data.totalViews) * 100 : 0;
    const profile2CommentRate = profile2Data.totalViews > 0 ? (profile2Data.totalComments / profile2Data.totalViews) * 100 : 0;
    
    const profile1LikeRate = profile1Data.totalViews > 0 ? (profile1Data.totalLikes / profile1Data.totalViews) * 100 : 0;
    const profile2LikeRate = profile2Data.totalViews > 0 ? (profile2Data.totalLikes / profile2Data.totalViews) * 100 : 0;
    
    const profile1ShareRate = profile1Data.totalViews > 0 ? (profile1Data.totalShares / profile1Data.totalViews) * 100 : 0;
    const profile2ShareRate = profile2Data.totalViews > 0 ? (profile2Data.totalShares / profile2Data.totalViews) * 100 : 0;

    const maxCommentRate = Math.max(profile1CommentRate, profile2CommentRate);
    const maxLikeRate = Math.max(profile1LikeRate, profile2LikeRate);
    const maxShareRate = Math.max(profile1ShareRate, profile2ShareRate);
    const maxEngagement = Math.max(profile1Data.engagementRate, profile2Data.engagementRate);
    const maxViews = Math.max(profile1Data.totalViews, profile2Data.totalViews);

    return [
      {
        metric: 'Comments',
        profile1: maxCommentRate > 0 ? (profile1CommentRate / maxCommentRate) * 100 : 0,
        profile2: maxCommentRate > 0 ? (profile2CommentRate / maxCommentRate) * 100 : 0
      },
      {
        metric: 'Likes',
        profile1: maxLikeRate > 0 ? (profile1LikeRate / maxLikeRate) * 100 : 0,
        profile2: maxLikeRate > 0 ? (profile2LikeRate / maxLikeRate) * 100 : 0
      },
      {
        metric: 'Shares',
        profile1: maxShareRate > 0 ? (profile1ShareRate / maxShareRate) * 100 : 0,
        profile2: maxShareRate > 0 ? (profile2ShareRate / maxShareRate) * 100 : 0
      },
      {
        metric: 'Overall Eng.',
        profile1: maxEngagement > 0 ? (profile1Data.engagementRate / maxEngagement) * 100 : 0,
        profile2: maxEngagement > 0 ? (profile2Data.engagementRate / maxEngagement) * 100 : 0
      },
      {
        metric: 'Reach',
        profile1: maxViews > 0 ? (profile1Data.totalViews / maxViews) * 100 : 0,
        profile2: maxViews > 0 ? (profile2Data.totalViews / maxViews) * 100 : 0
      }
    ];
  }, [profile1Data, profile2Data]);

  // formatTooltipValue removed as unused

  const getWinner = (value1: number, value2: number) => {
    if (value1 > value2) return 'profile1';
    if (value2 > value1) return 'profile2';
    return 'tie';
  };

  // Generate competitor analysis insights directly
  const getFallbackCompetitorAnalysis = (
    profile1: any,
    profile2: any
  ): CompetitorAnalysisInsight => {
    const winner = profile1.totalEMV > profile2.totalEMV ? profile1 : profile2;
    const loser = profile1.totalEMV > profile2.totalEMV ? profile2 : profile1;
    
    return {
      summary: `${winner.fullName} outperforms ${loser.fullName} with ${((winner.totalEMV / loser.totalEMV - 1) * 100).toFixed(1)}% higher total EMV and ${((winner.engagementRate / loser.engagementRate - 1) * 100).toFixed(1)}% better engagement rate.`,
      strengthsWeaknesses: {
        profile1Strengths: profile1.totalEMV > profile2.totalEMV ? ['Higher EMV generation', 'Better engagement rates'] : ['More consistent posting'],
        profile1Weaknesses: profile1.totalEMV < profile2.totalEMV ? ['Lower EMV performance', 'Needs engagement optimization'] : ['Could increase posting frequency'],
        profile2Strengths: profile2.totalEMV > profile1.totalEMV ? ['Higher EMV generation', 'Better engagement rates'] : ['More consistent posting'],
        profile2Weaknesses: profile2.totalEMV < profile1.totalEMV ? ['Lower EMV performance', 'Needs engagement optimization'] : ['Could increase posting frequency']
      },
      recommendations: [
        'Focus on content types that generate higher engagement',
        'Analyze top-performing posts for successful patterns',
        'Optimize posting schedule based on audience activity'
      ],
      keyFindings: [
        `${winner.fullName} has ${((winner.averageEMV / loser.averageEMV - 1) * 100).toFixed(1)}% higher average EMV per post`,
        `Engagement rates differ by ${Math.abs(profile1.engagementRate - profile2.engagementRate).toFixed(1)} percentage points`,
        `Sponsored content represents ${((winner.sponsoredEMV / winner.totalEMV) * 100).toFixed(1)}% of ${winner.fullName}'s total EMV`
      ]
    };
  };

  // generateAIInsights removed as unused - insights auto-generated in useEffect

  // Reset insights when profiles change
  React.useEffect(() => {
    setAiInsights(null);
  }, [selectedProfile1, selectedProfile2]);

  // Auto-generate AI insights when both profile data are available
  React.useEffect(() => {
    const autoGenerateInsights = async () => {
      if (!profile1Data || !profile2Data) return;
      if (loadingProfileData) return; // Wait for profile data to finish loading

      setLoadingInsights(true);

      try {
        // Small delay to ensure all state updates are complete
        await new Promise(resolve => setTimeout(resolve, 1000));

        const insights = getFallbackCompetitorAnalysis(profile1Data, profile2Data);
        setAiInsights(insights);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to auto-generate AI insights';
        showError('Failed to Generate AI Insights', message);
      } finally {
        setLoadingInsights(false);
      }
    };

    autoGenerateInsights();
  }, [profile1Data, profile2Data, loadingProfileData, posts, selectedAccount1, selectedAccount2]);

  // Reusable account loader
  const loadCustomerAccounts = useCallback(async (showLoading = true) => {
    if (!currentCustomerId) return;

    if (showLoading) setLoadingAccounts(true);

    try {
      const response = await socialMediaService.getCustomerAccounts(currentCustomerId, undefined, true);

      if (response.success && response.data && response.data.customer && response.data.customer.platformAccounts) {
        // Filter accounts with categoryId 1 or 3
        const filteredAccounts = response.data.customer.platformAccounts
          .filter((account: any) => account.categoryId === 1 || account.categoryId === 3)
          .sort((a: any, b: any) => (a.username || '').localeCompare(b.username || ''));
        setCustomerAccounts(filteredAccounts);
      } else {
        showError('Failed to Load Accounts', response.message || 'Failed to load customer accounts');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load accounts';
      showError('Failed to Load Accounts', message);
    } finally {
      setLoadingAccounts(false);
    }
  }, [currentCustomerId, showError]);

  // Load customer accounts when component mounts or customerId changes
  React.useEffect(() => {
    loadCustomerAccounts();
  }, [loadCustomerAccounts]);

  // Refresh account dropdowns when a scrape job completes
  const lastCompletedAt = useScrapeOperationStore((state) => state.lastCompletedAt);
  React.useEffect(() => {
    if (lastCompletedAt) {
      loadCustomerAccounts(false);
    }
  }, [lastCompletedAt, loadCustomerAccounts]);

  // Load profile data when both profiles are selected
  React.useEffect(() => {
    const loadProfileData = async () => {
      if (!selectedAccount1 || !selectedAccount2 ||
          selectedProfile1 === '__create_new__' || selectedProfile2 === '__create_new__') {
        return;
      }

      setLoadingProfileData(true);

      try {
        // Clear previous data
        setProfile1Analytics(null);
        setProfile2Analytics(null);
        // Posts state removed

        // Make separate API calls for each account to get individual data
        // First, load data for account 1
        await Promise.all([
          loadDashboardAnalytics({
            accountIds: [selectedAccount1.id],
            categoryId: selectedAccount1.categoryId
          }),
          loadTopPosts({
            accountIds: [selectedAccount1.id],
            categoryId: selectedAccount1.categoryId
          })
        ]);

        // Access the current store state to capture account 1 data
        const storeState = useSocialMediaStore.getState();
        setProfile1Analytics(storeState.dashboardAnalytics);
        // Profile 1 posts loaded from store

        // Then, load data for account 2
        await Promise.all([
          loadDashboardAnalytics({
            accountIds: [selectedAccount2.id],
            categoryId: selectedAccount2.categoryId
          }),
          loadTopPosts({
            accountIds: [selectedAccount2.id],
            categoryId: selectedAccount2.categoryId
          })
        ]);

        // Access the current store state to capture account 2 data
        const storeState2 = useSocialMediaStore.getState();
        setProfile2Analytics(storeState2.dashboardAnalytics);
        // Profile 2 posts loaded from store

      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load profile data';
        showError('Failed to Load Profile Data', message);
      } finally {
        setLoadingProfileData(false);
      }
    };

    loadProfileData();
  }, [selectedProfile1, selectedProfile2, selectedAccount1, selectedAccount2]);

  // Handle Add Account from drawer
  const handleAddProfile = async () => {
    if (!currentCustomerId || !newProfileUsername.trim()) return;

    setIsCreatingProfile(true);

    try {
      const scrapeRequest: ScrapeRequest = {
        customer_id: currentCustomerId,
        customer_account_id: `new-${newProfilePlatform}-${Date.now()}`,
        platform_id: scrapingService.getPlatformId(newProfilePlatform),
        platform_username: newProfileUsername.trim()
      };

      const response = await scrapingService.requestScraping(scrapeRequest);
      
      if (response.success && response.operation_id) {
        // Track operation for polling notifications
        useScrapeOperationStore.getState().addOperation({
          operationId: response.operation_id,
          username: newProfileUsername.trim(),
          platform: newProfilePlatform,
          startedAt: Date.now()
        });

        // Reset form
        setNewProfileUsername('');
        setNewProfilePlatform('instagram');
        setIsAddProfileDrawerOpen(false);

        // Show success toast
        showSuccess('Account Creation Started', `Scraping started for @${newProfileUsername.trim()} on ${newProfilePlatform}. You'll be notified when it completes.`);
      } else {
        showError('Failed to Start Profile Creation', response.message || 'Failed to start scraping profile');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create profile';
      showError('Failed to Create Account', message);
    } finally {
      setIsCreatingProfile(false);
    }
  };


  // const availableProfiles = profiles.filter(p => 
  //   p.username !== selectedProfile1 && p.username !== selectedProfile2
  // );

  // Always show the interface - users can create new profiles if needed

  // Show loading state while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Check if user has access to competitive analysis
  if (!hasFeature('competitor_benchmarking')) {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="max-w-md w-full">
          <Card className="p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gray-100 rounded-full">
                <Lock className="h-12 w-12 text-gray-400" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Upgrade to Access Competitor Benchmarking
            </h2>
            
            <p className="text-gray-600 mb-6">
              Competitor Benchmarking is not available in your current subscription tier. 
              Upgrade your plan to unlock powerful insights and benchmark your performance 
              against competitors.
            </p>
            
            <div className="space-y-3 text-left mb-8">
              <h3 className="font-semibold text-gray-900">With Competitor Benchmarking, you can:</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <GitCompare className="h-5 w-5 text-indigo-500 mr-2 mt-0.5" />
                  <span>Compare performance metrics across multiple accounts</span>
                </li>
                <li className="flex items-start">
                  <TrendingUp className="h-5 w-5 text-indigo-500 mr-2 mt-0.5" />
                  <span>Track engagement rates and growth trends</span>
                </li>
                <li className="flex items-start">
                  <Brain className="h-5 w-5 text-indigo-500 mr-2 mt-0.5" />
                  <span>Get AI-powered insights and recommendations</span>
                </li>
                <li className="flex items-start">
                  <Target className="h-5 w-5 text-indigo-500 mr-2 mt-0.5" />
                  <span>Identify content strategy opportunities</span>
                </li>
              </ul>
            </div>
            
            <button
              className="w-full inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white border border-transparent rounded-lg transition-colors shadow-sm"
            >
              Upgrade Your Plan
            </button>
            
            <p className="text-sm text-gray-500 mt-4">
              Contact your administrator or support team for upgrade options
            </p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Tab Navigation */}
      <div className="flex items-center justify-between bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner">
        <div className="flex space-x-1">
          <button
            onClick={() => setActiveTab('analysis')}
            className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
              activeTab === 'analysis'
                ? 'bg-white text-blue-600 shadow-md border border-white/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <GitCompare className="h-3.5 w-3.5 mr-1.5" />
            Analysis
          </button>
          <button
            onClick={() => setActiveTab('downloads')}
            className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all ${
              activeTab === 'downloads'
                ? 'bg-white text-blue-600 shadow-md border border-white/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
          >
            <ListTodo className="h-3.5 w-3.5 mr-1.5" />
            Jobs
          </button>
        </div>
        {permissions?.effectiveLimits && (
          <div className="flex items-center px-3">
            <span className={`text-xs font-medium ${atCompetitorLimit ? 'text-red-600' : 'text-gray-500'}`}>
              Competitors: {competitorCount} of {maxCompetitors} used
            </span>
          </div>
        )}
      </div>

      {/* Analysis Tab */}
      {activeTab === 'analysis' && (
        <div className="space-y-8">
          {/* Profile Selection */}
          <Card className="p-4 bg-gradient-to-r from-indigo-50 via-white to-purple-50 border border-indigo-200 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-2 h-5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full mr-3"></div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Competitor Benchmarking</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Compare social media performance metrics across accounts
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsAddProfileDrawerOpen(true)}
                disabled={atCompetitorLimit}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white border border-transparent rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title={atCompetitorLimit ? `You have reached your limit of ${maxCompetitors} competitor accounts` : undefined}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add Account
              </button>
            </div>


        {loadingAccounts && (
          <div className="mb-4 flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-avi-primary"></div>
            <span className="ml-2 text-xs text-gray-600">Loading accounts...</span>
          </div>
        )}
        
        <div className="flex flex-col md:flex-row items-center gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Account 1</label>
            <select
              value={selectedProfile1}
              onChange={(e) => {
                setSelectedProfile1(e.target.value);
                // Reset profile 2 if platforms don't match
                const account1 = getSelectedAccount(e.target.value);
                const account2 = getSelectedAccount(selectedProfile2);
                if (account1 && account2 && account1.platformId !== account2.platformId) {
                  setSelectedProfile2('');
                }
              }}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-avi-primary focus:border-transparent bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
              disabled={loadingAccounts}
            >
              <option value="">{customerAccounts.length === 0 ? 'Add a new account' : 'Select or create new account'}</option>
              {customerAccounts.filter(account => account.id !== selectedProfile2).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.displayName} (@{account.username}) - {account.platformDisplayName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-center pt-5 md:pt-6">
            <div className="p-2 bg-gray-100 rounded-full">
              <GitCompare className="h-4 w-4 text-gray-600" />
            </div>
          </div>

          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Account 2</label>
            <select
              value={selectedProfile2}
              onChange={(e) => setSelectedProfile2(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-avi-primary focus:border-transparent bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
              disabled={loadingAccounts}
            >
              <option value="">{customerAccounts.length === 0 ? 'Add a new account' : 'Select or create new account'}</option>
              {(() => {
                const account1 = getSelectedAccount(selectedProfile1);
                const availableAccounts = account1 
                  ? customerAccounts.filter(account => 
                      account.id !== selectedProfile1 && 
                      account.platformId === account1.platformId
                    )
                  : customerAccounts.filter(account => account.id !== selectedProfile1);
                
                return availableAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.displayName} (@{account.username}) - {account.platformDisplayName}
                  </option>
                ));
              })()}
            </select>
          </div>
        </div>
      </Card>

      {/* Loading indicator for profile data */}
      {loadingProfileData && (
        <Card className="p-4">
          <div className="flex items-center justify-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-avi-primary"></div>
            <span className="text-xs text-gray-600">Loading account data...</span>
          </div>
        </Card>
      )}

      {/* Error message for profile data */}

      {/* Show comparison results if we have data for both profiles */}
      {profile1Data && profile2Data && !loadingProfileData ? (
        <>
          {/* Quick Stats Comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-white border border-blue-200 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-xs">1</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs">{profile1Data.fullName}</h4>
                </div>
                <div className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full font-medium">
                  @{profile1Data.username}
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-1 border-b border-blue-100">
                  <span className="text-gray-600 font-medium text-xs">Total EMV</span>
                  <span className="font-semibold text-blue-700 text-xs">{formatEMV(profile1Data.totalEMV)}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-blue-100">
                  <span className="text-gray-600 font-medium text-xs">Average EMV</span>
                  <span className="font-semibold text-blue-700 text-xs">{formatEMV(profile1Data.averageEMV)}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-blue-100">
                  <span className="text-gray-600 font-medium text-xs">Total Posts</span>
                  <span className="font-semibold text-blue-700 text-xs">{profile1Data.totalPosts.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600 font-medium text-xs">Engagement Rate</span>
                  <span className="font-semibold text-blue-700 text-xs">{profile1Data.engagementRate.toFixed(1)}%</span>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-violet-50 to-white border border-violet-200 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-7 h-7 bg-violet-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-xs">2</span>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-xs">{profile2Data.fullName}</h4>
                </div>
                <div className="text-xs text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full font-medium">
                  @{profile2Data.username}
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-1 border-b border-violet-100">
                  <span className="text-gray-600 font-medium text-xs">Total EMV</span>
                  <span className="font-semibold text-violet-700 text-xs">{formatEMV(profile2Data.totalEMV)}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-violet-100">
                  <span className="text-gray-600 font-medium text-xs">Average EMV</span>
                  <span className="font-semibold text-violet-700 text-xs">{formatEMV(profile2Data.averageEMV)}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-violet-100">
                  <span className="text-gray-600 font-medium text-xs">Total Posts</span>
                  <span className="font-semibold text-violet-700 text-xs">{profile2Data.totalPosts.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600 font-medium text-xs">Engagement Rate</span>
                  <span className="font-semibold text-violet-700 text-xs">{profile2Data.engagementRate.toFixed(1)}%</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Comparison Summary - Moved up from below */}
          <Card className="p-4 bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm">
            <h4 className="text-xs font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
              Comparison Summary
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {comparisonData.map((item) => {
                const winner = getWinner(item.profile1, item.profile2);
                const formatValue = (value: number, metric: string) => {
                  if (metric.includes('EMV')) {
                    return formatEMV(value);
                  }
                  if (metric === 'Engagement Rate') {
                    return `${value.toFixed(1)}%`;
                  }
                  return value.toLocaleString();
                };
                
                const maxValue = Math.max(item.profile1, item.profile2);
                const profile1Percentage = maxValue > 0 ? (item.profile1 / maxValue) * 100 : 0;
                const profile2Percentage = maxValue > 0 ? (item.profile2 / maxValue) * 100 : 0;
                
                return (
                  <div key={item.metric} className="relative text-center p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-violet-500 rounded-t-xl"></div>

                    <h5 className="font-semibold text-gray-900 mb-3 text-xs">{item.metric}</h5>

                    {/* Visual comparison bars - Vertical */}
                    <div className="mb-3 flex justify-center items-end space-x-6 h-24">
                      {/* Account 1 vertical bar */}
                      <div className="flex flex-col items-center space-y-2">
                        <div className="text-xs text-gray-500 text-center">
                          {formatValue(item.profile1, item.metric)}
                        </div>
                        <div className="w-8 h-16 bg-gray-100 rounded-t-lg flex flex-col-reverse">
                          <div 
                            className={`w-full rounded-t-lg transition-all duration-700 ease-out ${winner === 'profile1' ? 'bg-blue-500' : 'bg-blue-300'}`}
                            style={{ height: `${profile1Percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-blue-700 font-medium text-center">
                          {profile1Data.fullName?.split(' ')[0] || profile1Data.username || 'Profile 1'}
                        </div>
                      </div>
                      
                      {/* VS indicator */}
                      <div className="flex items-center justify-center text-gray-400 text-xs font-bold self-center">
                        VS
                      </div>
                      
                      {/* Account 2 vertical bar */}
                      <div className="flex flex-col items-center space-y-2">
                        <div className="text-xs text-gray-500 text-center">
                          {formatValue(item.profile2, item.metric)}
                        </div>
                        <div className="w-8 h-16 bg-gray-100 rounded-t-lg flex flex-col-reverse">
                          <div 
                            className={`w-full rounded-t-lg transition-all duration-700 ease-out ${winner === 'profile2' ? 'bg-violet-500' : 'bg-violet-300'}`}
                            style={{ height: `${profile2Percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-violet-700 font-medium text-center">
                          {profile2Data.fullName?.split(' ')[0] || profile2Data.username || 'Profile 2'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Winner indicator */}
                    {winner === 'tie' ? (
                      <div className="text-amber-600 flex flex-col items-center">
                        <Award className="h-4 w-4 mb-1" />
                        <span className="text-xs font-medium">Tie</span>
                      </div>
                    ) : (
                      <div className={`flex flex-col items-center ${winner === 'profile1' ? 'text-blue-600' : 'text-violet-600'}`}>
                        <TrendingUp className="h-4 w-4 mb-1" />
                        <span className="text-xs font-semibold">
                          {winner === 'profile1' 
                            ? (profile1Data.fullName?.split(' ')[0] || profile1Data.username || 'Profile 1')
                            : (profile2Data.fullName?.split(' ')[0] || profile2Data.username || 'Profile 2')
                          } Wins
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Radar Analysis Panel */}
          <Card className="p-4 bg-gradient-to-br from-gray-50 to-white border border-gray-200 shadow-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-2 h-4 bg-gradient-to-b from-indigo-500 to-violet-500 rounded-full mr-3"></div>
              Radar Analysis
            </h4>
            
            {/* Three Radar Charts - Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Performance Overview Radar */}
              <div className="p-4 bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-xl shadow-sm">
                <h5 className="text-xs font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
                  Performance Overview
                </h5>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={performanceRadarData}>
                      <PolarGrid stroke="#e5e7eb" strokeWidth={1} />
                      <PolarAngleAxis 
                        dataKey="metric" 
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: '#9ca3af', fontSize: 8 }}
                        tickCount={5}
                      />
                      <Radar
                        name={profile1Data.fullName}
                        dataKey="profile1"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.2}
                        strokeWidth={2}
                      />
                      <Radar
                        name={profile2Data.fullName}
                        dataKey="profile2"
                        stroke="#8b5cf6"
                        fill="#8b5cf6"
                        fillOpacity={0.2}
                        strokeWidth={2}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '11px'
                        }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Content Strategy Radar */}
              <div className="p-4 bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-xl shadow-sm">
                <h5 className="text-xs font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                  Content Strategy
                </h5>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={contentStrategyRadarData}>
                      <PolarGrid stroke="#e5e7eb" strokeWidth={1} />
                      <PolarAngleAxis 
                        dataKey="metric" 
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: '#9ca3af', fontSize: 8 }}
                        tickCount={5}
                      />
                      <Radar
                        name={profile1Data.fullName}
                        dataKey="profile1"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.2}
                        strokeWidth={2}
                      />
                      <Radar
                        name={profile2Data.fullName}
                        dataKey="profile2"
                        stroke="#8b5cf6"
                        fill="#8b5cf6"
                        fillOpacity={0.2}
                        strokeWidth={2}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '11px'
                        }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Audience Engagement Radar */}
              <div className="p-4 bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-xl shadow-sm">
                <h5 className="text-xs font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-2 h-2 bg-violet-500 rounded-full mr-2"></div>
                  Audience Engagement
                </h5>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={audienceEngagementRadarData}>
                      <PolarGrid stroke="#e5e7eb" strokeWidth={1} />
                      <PolarAngleAxis 
                        dataKey="metric" 
                        tick={{ fill: '#6b7280', fontSize: 10 }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]} 
                        tick={{ fill: '#9ca3af', fontSize: 8 }}
                        tickCount={5}
                      />
                      <Radar
                        name={profile1Data.fullName}
                        dataKey="profile1"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.2}
                        strokeWidth={2}
                      />
                      <Radar
                        name={profile2Data.fullName}
                        dataKey="profile2"
                        stroke="#8b5cf6"
                        fill="#8b5cf6"
                        fillOpacity={0.2}
                        strokeWidth={2}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '11px'
                        }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </Card>


          {/* AI Insights Section */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Brain className="h-3.5 w-3.5 text-purple-600" />
                <h4 className="text-sm font-semibold text-gray-900">AI Strategic Analysis</h4>
              </div>
              {loadingInsights && !aiInsights && (
                <div className="flex items-center space-x-2 text-purple-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  <span className="text-xs">Analyzing...</span>
                </div>
              )}
            </div>

            {aiInsights ? (
              <div className="space-y-4">
                {/* Executive Summary */}
                <div className="bg-purple-50 p-3 rounded-lg">
                  <h5 className="text-xs font-semibold text-purple-900 mb-1">Executive Summary</h5>
                  <p className="text-xs text-purple-800">{aiInsights.summary}</p>
                </div>

                {/* Strengths & Weaknesses */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                      <div className="w-2.5 h-2.5 bg-blue-500 rounded-full mr-2"></div>
                      {profile1Data.fullName}
                    </h5>
                    <div className="space-y-2">
                      <div>
                        <h6 className="text-xs font-medium text-violet-700 mb-1 flex items-center">
                          <Zap className="h-3.5 w-3.5 mr-1" />
                          Strengths
                        </h6>
                        <ul className="space-y-1">
                          {aiInsights.strengthsWeaknesses.profile1Strengths.map((strength, index) => (
                            <li key={index} className="text-xs text-gray-700 flex items-start">
                              <span className="text-violet-500 mr-2">•</span>
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-xs font-medium text-red-700 mb-1 flex items-center">
                          <Target className="h-3.5 w-3.5 mr-1" />
                          Areas for Improvement
                        </h6>
                        <ul className="space-y-1">
                          {aiInsights.strengthsWeaknesses.profile1Weaknesses.map((weakness, index) => (
                            <li key={index} className="text-xs text-gray-700 flex items-start">
                              <span className="text-red-500 mr-2">•</span>
                              {weakness}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                      <div className="w-2.5 h-2.5 bg-violet-500 rounded-full mr-2"></div>
                      {profile2Data.fullName}
                    </h5>
                    <div className="space-y-2">
                      <div>
                        <h6 className="text-xs font-medium text-violet-700 mb-1 flex items-center">
                          <Zap className="h-3.5 w-3.5 mr-1" />
                          Strengths
                        </h6>
                        <ul className="space-y-1">
                          {aiInsights.strengthsWeaknesses.profile2Strengths.map((strength, index) => (
                            <li key={index} className="text-xs text-gray-700 flex items-start">
                              <span className="text-violet-500 mr-2">•</span>
                              {strength}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-xs font-medium text-red-700 mb-1 flex items-center">
                          <Target className="h-3.5 w-3.5 mr-1" />
                          Areas for Improvement
                        </h6>
                        <ul className="space-y-1">
                          {aiInsights.strengthsWeaknesses.profile2Weaknesses.map((weakness, index) => (
                            <li key={index} className="text-xs text-gray-700 flex items-start">
                              <span className="text-red-500 mr-2">•</span>
                              {weakness}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strategic Recommendations */}
                <div>
                  <h5 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
                    <Lightbulb className="h-4 w-4 text-yellow-500 mr-2" />
                    Strategic Recommendations
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {aiInsights.recommendations.map((recommendation, index) => (
                      <div key={index} className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                        <p className="text-xs text-yellow-800">{recommendation}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Findings */}
                <div>
                  <h5 className="text-xs font-semibold text-gray-900 mb-2">Key Findings</h5>
                  <div className="space-y-2">
                    {aiInsights.keyFindings.map((finding, index) => (
                      <div key={index} className="flex items-start space-x-3 p-2.5 bg-gray-50 rounded-lg">
                        <div className="flex-shrink-0 w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-semibold text-purple-600">{index + 1}</span>
                        </div>
                        <p className="text-xs text-gray-700">{finding}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">AI insights will automatically generate when profile data is loaded</p>
                <p className="text-sm text-gray-400">Claude AI will analyze performance patterns, identify strengths & weaknesses, and provide actionable recommendations</p>
              </div>
            )}
          </Card>
        </>
      ) : (
        /* Show message when profiles aren't ready for comparison */
        <Card className="p-4">
          <div className="text-center py-8">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 mb-2">Ready to Compare Accounts</h3>
            <p className="text-xs text-gray-500 max-w-md mx-auto">
              Select two accounts or create new ones by entering usernames to see their competitor benchmarking with charts and insights.
            </p>
          </div>
        </Card>
      )}
        </div>
      )}

      {/* Add Account Drawer */}
      <Drawer
        isOpen={isAddProfileDrawerOpen}
        onClose={() => {
          setIsAddProfileDrawerOpen(false);
          setNewProfileUsername('');
          setNewProfilePlatform('instagram');
        }}
        title="Add New Competitor Account"
        width="lg"
      >
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-800">
              <strong>Note:</strong> Account creation involves scraping social media data, which can take several minutes to complete.
              You can check the status on the Jobs tab. The account will appear in the dropdowns automatically once scraping finishes.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Platform *
            </label>
            <select
              value={newProfilePlatform}
              onChange={(e) => setNewProfilePlatform(e.target.value as 'instagram' | 'tiktok')}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={isCreatingProfile}
            >
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Username *
            </label>
            <Input
              type="text"
              placeholder="Enter username (without @)"
              value={newProfileUsername}
              onChange={(e) => setNewProfileUsername(e.target.value)}
              disabled={isCreatingProfile}
              className="w-full text-xs"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the username without the @ symbol (e.g., "johndoe" not "@johndoe")
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setIsAddProfileDrawerOpen(false);
                setNewProfileUsername('');
                setNewProfilePlatform('instagram');
              }}
              disabled={isCreatingProfile}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleAddProfile}
              disabled={isCreatingProfile || !newProfileUsername.trim()}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white border border-transparent rounded-lg transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreatingProfile ? 'Creating...' : 'Create Account'}
            </button>
          </div>
        </div>
      </Drawer>

      {/* Jobs Tab */}
      {activeTab === 'downloads' && <Downloads />}
    </div>
  );
};

export default CompetitiveAnalysis;